/**
 * Marquee — seamless infinite scroll via RAF.
 * Liquid renders 4 item sets; JS adds more if viewport is wider, then loops one set width.
 */
const MARQUEE_SELECTORS = {
  list: '[data-marquee-list]',
  viewport: '[data-marquee-viewport]',
  item: '[data-marquee-item]',
  clone: '[data-marquee-clone]',
  jsClone: '[data-marquee-js-clone]',
  pauseButton: '[data-marquee-pause]',
  pauseLabel: '[data-marquee-pause-label]',
};

class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.list = null;
    this.viewport = null;
    this.pauseButton = null;
    this.pauseLabel = null;
    this.resizeObserver = null;
    this.rafId = null;
    this.lastFrame = 0;
    this.offset = 0;
    this.loopWidth = 0;
    this.speed = 60;
    this.isPaused = false;
    this.userPaused = false;

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onPauseToggle = this.onPauseToggle.bind(this);
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.pauseButton = this.querySelector(MARQUEE_SELECTORS.pauseButton);
    this.pauseLabel = this.querySelector(MARQUEE_SELECTORS.pauseLabel);

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', this.onPauseToggle);
    }

    if (this.prefersReducedMotion()) {
      this.classList.add('bst-marquee--paused');
      this.setPauseButtonState(true, true);
      return;
    }

    this.init();
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    this.resetTrack();
    this.destroy();
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    if (this.pauseButton) {
      this.pauseButton.removeEventListener('click', this.onPauseToggle);
    }
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onSectionLoad = (event) => {
    const section = this.closest('.shopify-section');
    if (section && event.target === section) {
      this.resetTrack();
      this.init();
    }
  };

  onVisibilityChange = () => {
    this.updatePauseFlag();
    this.resumeIfNeeded();
  };

  onMouseEnter() {
    this.setTransientPause(true);
  }

  onMouseLeave() {
    this.setTransientPause(false);
  }

  onFocusIn(event) {
    if (event.target.closest(MARQUEE_SELECTORS.pauseButton)) return;
    this.setTransientPause(true);
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this.setTransientPause(false);
  }

  onPauseToggle() {
    this.userPaused = !this.userPaused;
    this.classList.toggle('bst-marquee--user-paused', this.userPaused);
    this.setPauseButtonState(this.userPaused);
    this.updatePauseFlag();
    this.resumeIfNeeded();
  }

  setPauseButtonState(isPaused, isDisabled = false) {
    if (!this.pauseButton) return;

    this.pauseButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
    this.pauseButton.disabled = isDisabled;

    if (this.pauseLabel) {
      this.pauseLabel.textContent = isPaused
        ? 'Play scrolling publications'
        : 'Pause scrolling publications';
    }
  }

  setTransientPause(shouldPause) {
    if (shouldPause) {
      this.classList.add('bst-marquee--paused');
    } else if (!this.userPaused) {
      this.classList.remove('bst-marquee--paused');
    }

    this.updatePauseFlag();
    this.resumeIfNeeded();
  }

  updatePauseFlag() {
    this.isPaused =
      this.userPaused ||
      this.classList.contains('bst-marquee--paused') ||
      document.hidden;
  }

  resumeIfNeeded() {
    if (!this.isPaused && !this.rafId && this.list && this.loopWidth > 0) {
      this.startAnimation();
    }
  }

  getPixelsPerSecond() {
    const speed = parseFloat(this.dataset.marqueeSpeed, 10);
    return Number.isFinite(speed) && speed > 0 ? speed : 60;
  }

  getOriginalItems(list) {
    return [...list.querySelectorAll('[data-marquee-copy="1"]')];
  }

  resetTrack() {
    const list = this.querySelector(MARQUEE_SELECTORS.list);
    if (!list) return;

    list.querySelectorAll(MARQUEE_SELECTORS.jsClone).forEach((node) => node.remove());
    delete list.dataset.marqueeBuilt;
    list.style.transform = '';
    this.offset = 0;
  }

  /** Width of one full set (copy 1) including trailing flex gap before copy 2. */
  measureLoopWidth(list) {
    const originals = this.getOriginalItems(list);
    if (originals.length === 0) return 0;

    const firstClone = list.querySelector('[data-marquee-copy="2"]');
    if (firstClone) {
      const listRect = list.getBoundingClientRect();
      const cloneRect = firstClone.getBoundingClientRect();
      const width = cloneRect.left - listRect.left;
      if (width > 0) return Math.round(width);
    }

    const gap = parseFloat(getComputedStyle(list).gap) || 0;
    let width = 0;
    originals.forEach((item, index) => {
      width += item.getBoundingClientRect().width;
      if (index < originals.length - 1) {
        width += gap;
      }
    });
    return Math.round(width + gap);
  }

  /** Add more sets on the right until track always fills viewport + buffer. */
  ensureTrackFill(list, viewport) {
    const originals = this.getOriginalItems(list);
    if (originals.length === 0) return;

    const loopWidth = this.measureLoopWidth(list);
    if (loopWidth <= 0) return;

    const viewportWidth = viewport.getBoundingClientRect().width;
    const minTrackWidth = viewportWidth + loopWidth * 2;
    let safety = 0;

    while (list.scrollWidth < minTrackWidth && safety < 12) {
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-marquee-js-clone', '');
        clone.setAttribute('data-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('data-marquee-copy');
        list.appendChild(clone);
      });
      safety += 1;
    }

    list.dataset.marqueeBuilt = 'true';
  }

  waitForImages(list) {
    const images = [...list.querySelectorAll('img')];

    const pending = images
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
      );

    return Promise.all(pending);
  }

  prepareTrack() {
    const list = this.querySelector(MARQUEE_SELECTORS.list);
    const viewport = this.querySelector(MARQUEE_SELECTORS.viewport);
    if (!list || !viewport) return false;

    list.querySelectorAll(MARQUEE_SELECTORS.jsClone).forEach((node) => node.remove());

    this.ensureTrackFill(list, viewport);
    this.loopWidth = this.measureLoopWidth(list);

    return this.loopWidth > 0;
  }

  async init() {
    this.destroyAnimation();

    const list = this.querySelector(MARQUEE_SELECTORS.list);
    this.viewport = this.querySelector(MARQUEE_SELECTORS.viewport);

    if (!list || !this.viewport) return;
    if (this.getOriginalItems(list).length < 1) return;

    this.list = list;
    this.speed = this.getPixelsPerSecond();

    await this.waitForImages(list);

    const ready = () => {
      if (!this.prepareTrack()) return;
      this.offset = this.offset % this.loopWidth;
      this.startAnimation();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(ready);
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.destroyObserver();
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = 0;
          const wasLoop = this.loopWidth;
          this.prepareTrack();
          if (wasLoop > 0 && this.loopWidth > 0) {
            this.offset = this.offset % this.loopWidth;
          }
        });
      });
      this.resizeObserver.observe(this.viewport);
    }
  }

  startAnimation() {
    if (this.rafId) return;
    this.updatePauseFlag();
    if (this.isPaused) return;
    this.lastFrame = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  tick(timestamp) {
    if (!this.list || this.loopWidth <= 0) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (!this.lastFrame) {
      this.lastFrame = timestamp;
    }

    const delta = Math.min((timestamp - this.lastFrame) / 1000, 0.05);
    this.lastFrame = timestamp;

    if (!this.isPaused && !this.userPaused && !this.classList.contains('bst-marquee--paused') && !document.hidden) {
      this.offset += this.speed * delta;

      while (this.offset >= this.loopWidth) {
        this.offset -= this.loopWidth;
      }

      this.list.style.transform = `translateX(${-this.offset}px)`;
    }

    this.rafId = requestAnimationFrame(this.tick);
  }

  destroyAnimation() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  destroyObserver() {
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = 0;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  destroy() {
    this.destroyAnimation();
    this.destroyObserver();
  }
}

customElements.define('marquee-component', MarqueeComponent);
